import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth, useCart } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  CreditCard, Shield, Truck, Package, AlertCircle, ArrowLeft, Lock, CheckCircle, Loader2, RefreshCw,
} from "lucide-react";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

const MA_TAX_RATE = 0.0625;

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, token } = useAuth();
  const { cart, loading: cartLoading } = useCart();
  const [loading, setLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [paymentToken, setPaymentToken] = useState(null);
  const [collectJSReady, setCollectJSReady] = useState(false);
  const [cardFieldsValid, setCardFieldsValid] = useState({ ccnumber: false, ccexp: false, cvv: false });
  const submitPendingRef = useRef(false);

  // Shipping state
  const [shippingMethod, setShippingMethod] = useState("");
  const [shippingCost, setShippingCost] = useState(0);
  const [shippingRates, setShippingRates] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesError, setRatesError] = useState("");

  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "MA",
    zip_code: "",
  });

  // Use cart from context directly — works for both authenticated and guest users
  const activeCart = {
    items: cart?.items || [],
    total: cart?.total || 0,
  };

  // Pre-fill email
  useEffect(() => {
    if (user?.email) setFormData((p) => ({ ...p, email: p.email || user.email }));
  }, [user]);

  // Fetch live UPS rates when address is complete
  const fetchRatesRef = useRef(null);
  useEffect(() => {
    const { city, state, zip_code } = formData;
    const hasItems = activeCart.items.length > 0;

    if (!city.trim() || !state || !zip_code || zip_code.length < 5 || !hasItems) {
      setShippingRates([]);
      setShippingMethod("");
      setShippingCost(0);
      setRatesError("");
      return;
    }

    if (fetchRatesRef.current) clearTimeout(fetchRatesRef.current);
    fetchRatesRef.current = setTimeout(() => {
      fetchShippingRates();
    }, 800);

    return () => { if (fetchRatesRef.current) clearTimeout(fetchRatesRef.current); };
  }, [formData.city, formData.state, formData.zip_code, activeCart.items.length]);

  const fetchShippingRates = async () => {
    setRatesLoading(true);
    setRatesError("");
    try {
      const cartItems = activeCart.items.map((i) => ({
        product_id: i.product_id,
        quantity: i.quantity,
      }));

      const resp = await axios.post(`${API}/shipping/rates`, {
        destination: {
          city: formData.city,
          state: formData.state,
          zip_code: formData.zip_code,
          country: "US",
        },
        cart_items: cartItems,
      });

      const rates = resp.data || [];
      setShippingRates(rates);

      if (rates.length > 0) {
        setShippingMethod(rates[0].method);
        setShippingCost(rates[0].price);
      } else {
        setShippingMethod("");
        setShippingCost(0);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || "Unable to fetch shipping rates. Please verify your address and try again.";
      setRatesError(detail);
      setShippingRates([]);
      setShippingMethod("");
      setShippingCost(0);
    } finally {
      setRatesLoading(false);
    }
  };

  // Configure Collect.js (NMI)
  useEffect(() => {
    const initCollect = () => {
      if (!window.CollectJS) return false;
      try {
        window.CollectJS.configure({
          variant: "inline",
          styleSniffer: false,
          fields: {
            ccnumber: { selector: "#nmi-card-number", title: "Card Number", placeholder: "0000 0000 0000 0000" },
            ccexp: { selector: "#nmi-card-exp", title: "Expiration", placeholder: "MM / YY" },
            cvv: { selector: "#nmi-card-cvv", title: "CVV", placeholder: "CVV" },
          },
          fieldsAvailableCallback: () => setCollectJSReady(true),
          validationCallback: (field, valid) => setCardFieldsValid((prev) => ({ ...prev, [field]: valid })),
          callback: (response) => {
            if (response.token) {
              setPaymentToken(response.token);
            } else {
              setPaymentError("Card tokenization failed. Please check your card details.");
              setLoading(false);
              submitPendingRef.current = false;
            }
          },
        });
        return true;
      } catch (e) {
        console.error("CollectJS configure error:", e);
        return false;
      }
    };

    if (!initCollect()) {
      const interval = setInterval(() => { if (initCollect()) clearInterval(interval); }, 500);
      return () => clearInterval(interval);
    }
  }, []);

  // Handle payment after token received
  useEffect(() => {
    if (paymentToken && submitPendingRef.current) {
      submitPendingRef.current = false;
      processPayment(paymentToken);
    }
  }, [paymentToken]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: null }));
  };

  const handleRateSelect = (rate) => {
    setShippingMethod(rate.method);
    setShippingCost(rate.price);
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.full_name.trim()) errs.full_name = "Full name is required";
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) errs.email = "Valid email is required";
    if (!formData.phone.trim()) errs.phone = "Phone is required";
    if (!formData.address_line1.trim()) errs.address_line1 = "Address is required";
    if (!formData.city.trim()) errs.city = "City is required";
    if (!formData.state) errs.state = "State is required";
    if (!formData.zip_code.trim() || !/^\d{5}(-\d{4})?$/.test(formData.zip_code)) errs.zip_code = "Valid ZIP code is required";
    if (activeCart.items.length === 0) errs.cart = "Cart is empty";
    if (!shippingMethod) errs.shipping = "Please select a shipping method";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const processPayment = async (payToken) => {
    setPaymentError("");
    try {
      const addr = {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        address_line1: formData.address_line1,
        address_line2: formData.address_line2 || "",
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        country: "United States",
      };

      let response;
      if (isAuthenticated) {
        response = await axios.post(`${API}/checkout/pay`, {
          payment_token: payToken,
          shipping_method: shippingMethod,
          shipping_cost: shippingCost,
          shipping_address: addr,
        }, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        const cartItems = activeCart.items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
        }));
        response = await axios.post(`${API}/checkout/guest`, {
          payment_token: payToken,
          shipping_method: shippingMethod,
          shipping_cost: shippingCost,
          shipping_address: addr,
          cart_items: cartItems,
        });
      }

      const result = response.data;
      if (result.success) {
        if (!isAuthenticated) localStorage.removeItem("guestCart");
        navigate(`/checkout/success?order_id=${result.order_id}&order_number=${result.order_number || ""}&total=${result.total}&txn=${result.transaction_id || ""}`);
      } else {
        setPaymentError(result.message || "Payment was not approved. Please try again.");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.message || "Payment processing failed. Please try again.";
      setPaymentError(msg);
    } finally {
      setLoading(false);
      setPaymentToken(null);
    }
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    if (!collectJSReady) {
      setPaymentError("Payment form is still loading. Please wait a moment.");
      return;
    }
    if (loading) return;

    setLoading(true);
    setPaymentError("");
    submitPendingRef.current = true;

    try {
      window.CollectJS.startPaymentRequest();
    } catch (err) {
      setPaymentError("Unable to process payment. Please refresh and try again.");
      setLoading(false);
      submitPendingRef.current = false;
    }
  };

  // Totals
  const taxAmount = (activeCart.total || 0) * MA_TAX_RATE;
  const grandTotal = (activeCart.total || 0) + shippingCost + taxAmount;

  const canSubmit = collectJSReady && activeCart.items.length > 0 && shippingMethod && !ratesLoading;

  if (cartLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (activeCart.items.length === 0 && !loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="empty-checkout">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Your cart is empty</h2>
          <Link to="/shop" className="text-primary hover:underline">Continue Shopping</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-8">
          <Link to="/cart" className="text-gray-500 hover:text-gray-700">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 uppercase">Checkout</h1>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Shipping Address */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h2 className="font-heading text-lg font-bold text-gray-900 uppercase mb-4 flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Shipping Address
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input id="full_name" name="full_name" value={formData.full_name} onChange={handleInputChange} placeholder="John Doe" className={`mt-1 ${errors.full_name ? "border-red-500" : ""}`} data-testid="fullname-input" />
                  {errors.full_name && <p className="text-red-500 text-sm mt-1">{errors.full_name}</p>}
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="you@email.com" className={`mt-1 ${errors.email ? "border-red-500" : ""}`} data-testid="email-input" />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input id="phone" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="(555) 123-4567" className={`mt-1 ${errors.phone ? "border-red-500" : ""}`} data-testid="phone-input" />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="address_line1">Street Address *</Label>
                  <Input id="address_line1" name="address_line1" value={formData.address_line1} onChange={handleInputChange} placeholder="123 Main St" className={`mt-1 ${errors.address_line1 ? "border-red-500" : ""}`} data-testid="address-input" />
                  {errors.address_line1 && <p className="text-red-500 text-sm mt-1">{errors.address_line1}</p>}
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="address_line2">Apt / Suite (Optional)</Label>
                  <Input id="address_line2" name="address_line2" value={formData.address_line2} onChange={handleInputChange} placeholder="Apt 4B" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input id="city" name="city" value={formData.city} onChange={handleInputChange} placeholder="Boston" className={`mt-1 ${errors.city ? "border-red-500" : ""}`} data-testid="city-input" />
                  {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <select id="state" name="state" value={formData.state} onChange={handleInputChange} className={`mt-1 w-full h-10 px-3 rounded-md border ${errors.state ? "border-red-500" : "border-gray-200"} bg-white`} data-testid="state-select">
                    {US_STATES.map((s) => (<option key={s} value={s}>{s}</option>))}
                  </select>
                  {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
                </div>
                <div>
                  <Label htmlFor="zip_code">ZIP Code *</Label>
                  <Input id="zip_code" name="zip_code" value={formData.zip_code} onChange={handleInputChange} placeholder="02101" className={`mt-1 ${errors.zip_code ? "border-red-500" : ""}`} data-testid="zip-input" />
                  {errors.zip_code && <p className="text-red-500 text-sm mt-1">{errors.zip_code}</p>}
                </div>
              </div>
            </div>

            {/* Shipping Method — Live UPS Rates */}
            <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="shipping-method-section">
              <h2 className="font-heading text-lg font-bold text-gray-900 uppercase mb-4 flex items-center gap-2">
                <Package className="w-5 h-5" />
                Shipping Method
              </h2>

              {ratesLoading && (
                <div className="flex items-center gap-3 py-8 justify-center text-gray-500" data-testid="shipping-rates-loading">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Fetching live UPS shipping rates...</span>
                </div>
              )}

              {ratesError && !ratesLoading && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="shipping-rates-error">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-red-700">{ratesError}</p>
                      <button
                        onClick={fetchShippingRates}
                        className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium flex items-center gap-1"
                        data-testid="retry-rates-btn"
                      >
                        <RefreshCw className="w-3.5 h-3.5" /> Try Again
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {!ratesLoading && !ratesError && shippingRates.length === 0 && (
                <div className="py-6 text-center text-gray-400" data-testid="shipping-rates-empty">
                  <Truck className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Enter your shipping address above to see available UPS shipping options.</p>
                </div>
              )}

              {!ratesLoading && shippingRates.length > 0 && (
                <div className="space-y-3" data-testid="shipping-rates-list">
                  {shippingRates.map((rate) => (
                    <label
                      key={rate.method}
                      className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-all ${shippingMethod === rate.method ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "border-gray-200 hover:border-gray-300"}`}
                      data-testid={`shipping-rate-${rate.service_code}`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="shipping"
                          value={rate.method}
                          checked={shippingMethod === rate.method}
                          onChange={() => handleRateSelect(rate)}
                          className="w-4 h-4 text-primary"
                        />
                        <div>
                          <p className="font-medium text-gray-900">{rate.name}</p>
                          <p className="text-sm text-gray-500">{rate.estimated_days}</p>
                        </div>
                      </div>
                      <span className="font-semibold text-gray-900">${rate.price.toFixed(2)}</span>
                    </label>
                  ))}
                </div>
              )}

              {errors.shipping && (
                <p className="text-red-500 text-sm mt-2">{errors.shipping}</p>
              )}
            </div>

            {/* Payment — NMI Collect.js */}
            <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="payment-section">
              <h2 className="font-heading text-lg font-bold text-gray-900 uppercase mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Payment
              </h2>
              <div className="flex items-center gap-2 mb-4 text-sm text-gray-500">
                <Lock className="w-4 h-4 text-green-600" />
                <span>Card details are securely handled by NMI. Your card data never touches our server.</span>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Card Number</Label>
                  <div id="nmi-card-number" data-testid="nmi-card-number" className="mt-1 h-11 border border-gray-200 rounded-md px-3 bg-white flex items-center"></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Expiration</Label>
                    <div id="nmi-card-exp" data-testid="nmi-card-exp" className="mt-1 h-11 border border-gray-200 rounded-md px-3 bg-white flex items-center"></div>
                  </div>
                  <div>
                    <Label>CVV</Label>
                    <div id="nmi-card-cvv" data-testid="nmi-card-cvv" className="mt-1 h-11 border border-gray-200 rounded-md px-3 bg-white flex items-center"></div>
                  </div>
                </div>
              </div>

              {!collectJSReady && (
                <div className="mt-3 text-sm text-amber-600 flex items-center gap-2">
                  <div className="animate-spin w-4 h-4 border-2 border-amber-600 border-t-transparent rounded-full"></div>
                  Loading secure payment fields...
                </div>
              )}

              {paymentError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-testid="payment-error">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700">{paymentError}</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column — Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-6 sticky top-24">
              <h2 className="font-heading text-lg font-bold text-gray-900 uppercase mb-4">Order Summary</h2>

              <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                {activeCart.items.map((item, index) => (
                  <div key={item.product_id || index} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                    <div className="w-12 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                      {item.image && <img src={item.image} alt={item.name} className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                      <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900">${((item.price || 0) * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>

              <div className="space-y-2 border-t border-gray-200 pt-4">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span data-testid="order-subtotal">${(activeCart.total || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Shipping {shippingMethod && shippingRates.length > 0 ? `(${shippingRates.find(r => r.method === shippingMethod)?.name || "UPS"})` : ""}</span>
                  <span data-testid="order-shipping">
                    {ratesLoading ? (
                      <span className="text-xs text-gray-400">Calculating...</span>
                    ) : shippingCost === 0 && !shippingMethod ? (
                      <span className="text-xs text-gray-400">--</span>
                    ) : (
                      `$${shippingCost.toFixed(2)}`
                    )}
                  </span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Tax (MA 6.25%)</span>
                  <span data-testid="order-tax">${taxAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t border-gray-200">
                  <span>Total</span>
                  <span data-testid="order-total">${grandTotal.toFixed(2)}</span>
                </div>
              </div>

              <Button
                onClick={handleSubmit}
                disabled={loading || !canSubmit}
                className="w-full mt-6 bg-primary hover:bg-primary/90 text-white py-6 text-lg"
                data-testid="place-order-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                    Processing Payment...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    Pay ${grandTotal.toFixed(2)}
                  </span>
                )}
              </Button>

              <div className="mt-6 space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Shield className="w-4 h-4 text-green-500" />
                  <span>Secure SSL Encrypted Payment</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Truck className="w-4 h-4 text-blue-500" />
                  <span>Real-time UPS shipping rates</span>
                </div>
              </div>

              {!isAuthenticated && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5" />
                    <p className="text-xs text-amber-700">
                      Checking out as guest. Order confirmation will be sent to your email.
                      <Link to="/auth" className="underline ml-1">Sign in</Link> to track orders and save addresses.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
